import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import { InputLabel, P3, P3Bold } from './Text';
import Icon from './Icons/Icon';
import colours from './Colours';
import DatePicker from '../../Common/TextEditableDatePicker';
import Stars from '../Stars';

// Example Usage - Standard Required Text Field with leading icon and trailing icon (use textarea prop if desired)
{/* <Input 
    required
    disabled={false}
    name="trim"
    label="Trim"
    leading_ic="Search"
    trailing_ic={this.state.vehicle_form.trim == '' ? "" : "Close"}
    trailing_ic_onClick={()=> {this.updateVehicleForm({target: {type: 'text', name: 'trim', value: ''}})}}
    onChange={this.updateVehicleForm}
    value={this.state.vehicle_form.trim}
    placeholder="Trim Example"
    variant={this.state.vehicle_form.trim == "3.3T Sport AWD 2" ? "warning" : "success"}
    message={this.state.vehicle_form.trim == "3.3T Sport AWD 2" ? "warning" : "success"}
/> */}

// Example Usage - Dropdown (pass the DropdownItems as children of the Input)
{/* <Input 
    dropdown
    name="trim"
    label="Trim"
    onChange={(e)=> {this.updateSelect({name: 'trim', value: e.currentTarget.dataset.id})}}
    value={this.state.vehicle_form.trim}
    placeholder="Trim Example"
    variant={this.state.vehicle_form.trim == "3.3T Sport AWD 2" ? "warning" : "success"}
    message={this.state.vehicle_form.trim == "3.3T Sport AWD 2" ? "warning" : "success"}
>
    <DropdownItem label="One" leading_ic="Mileage"/>
    <DropdownItem label="Two"/>
</Input> */}

//Example Usage - Datepicker (pass the datepicker prop and normal <DatePicker/> props)
{/* <Input
    datepicker
    value={this.state.content.date}
    name="date_of_expense"
    onChange={(val) => {
        if (moment(val.target.value).isValid() &&  moment(val.target.value).isAfter(moment())) {
            let temp = this.state.errors;
            temp.dateError = 'Post-dated';
            this.setState({ errors: temp, content: { ...this.state.content, date: val.target.value}});
        }  else {
            let temp = this.state.errors;
            temp.dateError = '';
            this.setState({ content: { ...this.state.content, date: val.target.value}});
        }   
    }}
    label="Date of Expense"
    id="new_expense_date"
    disabled={false}
    placeholder="MM-DD-YYYY"
    required
    trailing_ic="Calendar" 
    trailing_ic_style={{color: colours.primary.grey03}}
/> */}


const Input = React.forwardRef((props, ref) => {

    let {
        required,
        leading_ic,
        leading_ic_onClick,
        leading_ic_style,
        trailing_ic,
        trailing_ic_rawText,
        trailing_ic_onClick,
        trailing_ic_style,
        simple_dropdown,
        dropdown,
        dropdownLabel,
        datepicker,
        textarea,
        variant,
        message,
        inputclass,
        containerStyle,
        innerContainerStyle,
        dropdownContainerProps,
        dispatch,
        userSession,
        selectStars,
        ...otherProps
    } = props;

    const containerRef = React.useRef();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    
    useEffect(()=> {
        if (dropdown)
            document.addEventListener('mousedown', handleMouseDown);
        

        return (()=> {
            if (dropdown)
                document.removeEventListener('mousedown', handleMouseDown);
        });

    }, [])

    const getVariant = () => {
        if (variant == 'info' || variant == 'error' || variant == 'warning' || variant == 'success')
            return variant;
        
        else return '';
    }

    const getColor = (variant) => {
        if (variant == 'info')
            return colours.primary.blue;
        
        if (variant == 'success')
            return colours.accent.validate_green;
        
        if (variant == 'error')
            return colours.accent.error_red;

        if (variant == 'warning')
            return colours.accent.caution_yellow;
        
        return colours.primary.grey03;
    }

    const getIcon = (variant) => {
        if (variant == 'info' || variant == 'error' || variant == 'warning')
            return "AttentionForm";
        
        if (variant == 'success')
            return "SuccessForm"
        
        return '';
    }

    const handleMouseDown = (event) => {

        if (dropdown && containerRef.current && !containerRef.current.contains(event.target)) {
            setDropdownOpen(false);
        }
    }

    return (
        <>
            <div className="ds-input-outer-container" style={containerStyle} ref={containerRef}>
                {props.label && (
                    <div className="ds-input-label">
                        <InputLabel>{props.label}</InputLabel>
                        {required ? <div className="ds-input-required">*</div> : null}
                    </div>
                )}
                <div className="ds-input-inner-container" style={innerContainerStyle} ref={dropdown ? ref : null} onClick={dropdown && (!(props.disabled || props.readOnly)) ? ()=> {
                            setDropdownOpen(current => !current);
                        } : null}>
                    {textarea ?
                        null
                        : <div className={"ds-input-leading_ic" + (leading_ic_onClick && (!props.disabled) ? " onclick" : "" ) + (props.readOnly ? " readonly" : "")} onClick={leading_ic_onClick && (!props.disabled) ? leading_ic_onClick : null}>
                            {leading_ic ? <Icon name={leading_ic} size={20} style={leading_ic_style ? leading_ic_style : (props.disabled || props.readOnly) ? {color: colours.primary.grey02} : {color: colours.primary.grey01}}/> : null}
                        </div> 
                    }
                    {dropdown ? 
                        <div 
                            tabIndex={0}
                            className={"ds-input dropdown " 
                                + getVariant() 
                                + (leading_ic ? " pl-36" : "") 
                                + (trailing_ic ? " pr-36" : "") 
                                + (props.disabled ? " disabled" : "")
                                + (props.readOnly ? " readonly" : "") 
                                + " " + inputclass}>
                            {(props.dropdownLabel && props.dropdownLabel !== '') || (props.renderDropdownLabel) ?
                                props.renderDropdownLabel ? null : 
                                <div className="ds-input-dropdown-label">{props.dropdownLabel}</div> : 
                                    props.value && props.value !== '' ? 
                                        <div className="ds-input-dropdown-label">{props.value}</div>
                                        : <div className="ds-placeholder">{props.placeholder}</div>
                            }
                            {props.renderDropdownLabel}
                        </div>
                    :   textarea ? 
                        <textarea
                            className={"ds-textarea ds-input " + getVariant() + " " + inputclass}
                            tabIndex={0}
                            disabled={props.disabled}
                            name={props.name}
                            value={props.value}
                            readOnly={props.readOnly}
                            ref={ref}
                            onChange={props.onChange}
                            placeholder={props.placeholder ? props.placeholder : null}
                            {...otherProps}
                        />
                    :   selectStars ?
                        <div className={`ds-input ${inputclass}`}>
                            <Stars star={props.value} updateForm={props.onChange} {...props} />
                        </div>
                    :   datepicker ?
                            <DatePicker className={"ds-input" + " " + getVariant() + " " + inputclass} type="text" inputsRef={ref} {...otherProps}/>
                    :   simple_dropdown ?
                        <select
                            value={props.value}
                            className={"ds-input" + inputclass + (props.disabled ? " dropdown disabled" : "")}
                            onChange={(e) => {
                                props.onChange(e);
                            }}
                            {...otherProps}
                        >
                            {props.options.map((value, index) => (
                            <option value={value.value} key={value.value + "_" + index}>
                                {value.option}
                            </option>
                            ))}
                        </select>
                        : <input
                            type={"text"}
                            tabIndex={0}
                            className={"ds-input " + " " + getVariant() +  (leading_ic ? " pl-36" : "") + (trailing_ic ? " pr-36" : "") + " " + inputclass}
                            disabled={props.disabled}
                            name={props.name}
                            value={props.value}
                            readOnly={dropdown || props.readOnly}
                            ref={ref}
                            onChange={props.onChange}
                            placeholder={props.placeholder ? props.placeholder : null}
                            {...otherProps}
                        /> 
                    }
                    <div className={"ds-input-trailing_ic" + (dropdown ? " dropdown" : textarea ? " textarea" : "") + (trailing_ic_onClick && !props.disabled ? " onclick" : "" ) + (props.readOnly ? " readonly" : "")} onClick={trailing_ic_onClick && !props.disabled ? trailing_ic_onClick : null}>
                        { trailing_ic_rawText ?
                            <div style={trailing_ic_style ? trailing_ic_style : (props.disabled || props.readOnly) ? {color: colours.primary.grey02} : {color: colours.primary.grey01}}>{trailing_ic}</div>
                            : trailing_ic ? <Icon name={trailing_ic} size={20} style={trailing_ic_style ? trailing_ic_style : (props.disabled || props.readOnly) ? {color: colours.primary.grey02} : {color: colours.primary.grey01}}/> : null}
                    </div>
                    {dropdown ? 
                        <div className="ds-input-dropdown_ic">
                            <Icon name={dropdownOpen ? "ChevronUp" : "ChevronDown"} size={16} style={(props.disabled || props.readOnly) ? {color: colours.primary.grey02} : {color: colours.primary.grey01}}/>
                        </div> 
                    : null}
                </div>
                {dropdownOpen ? 
                    <div className="ds-input-dropdown-outer-container">
                        <div className="ds-input-dropdown-container" {...dropdownContainerProps}>
                            {props.children ? 
                                props.children.map((child) => {
                                if ((props.value == child.props.value) || (props.value == child.props.label)) {
                                    return (
                                        <React.Fragment key={child.props.value ? child.props.value : child.props.label}> 
                                            {React.cloneElement(child, { active: true, onChange: props.onChange, setDropdownOpen })} 
                                        </React.Fragment>
                                    )
                                } else {
                                    return (
                                        <React.Fragment key={child.props.value ? child.props.value : child.props.label}> 
                                            {React.cloneElement(child, { active: false, onChange: props.onChange, setDropdownOpen })}
                                        </React.Fragment>
                                    )
                                }
                            }) : null}
                        </div>
                    </div>
                : null}
            </div>
            {(getVariant() !== '') && message ? 
                <div className={"ds-input-message" + " " + getVariant()}>
                    <div className="ds-input-message-icon">
                        <Icon name={getIcon(getVariant())} size={16} style={{color: getColor(getVariant())}}/>
                    </div>
                    <P3 style={{color: colours.primary.grey01}}>
                        {message}    
                    </P3>
                </div>
            : null}
        </>
    )
});

Input.defaultProps = {
    label: '',
    required: false,
    leading_ic: '',
    leading_ic_onClick: null,
    leading_ic_style: null,
    trailing_ic: '',
    trailing_ic_rawText: false,
    trailing_ic_onClick: null,
    trailing_ic_style: null,
    placeholder: '',
    name: '',
    onChange: null,
    value: null,
    simple_dropdown: false,
    dropdown: false,
    dropdownLabel: '',
    renderDropdownLabel: null,
    datepicker: false,
    textarea: false,
    disabled: false,
    readOnly: false,
    variant: '',
    message: '',
    inputclass: '',
    containerStyle: null,
    innerContainerStyle: null,
    dropdownContainerProps: null
};

Input.propTypes = {
    label: PropTypes.string,
    required: PropTypes.bool,
    leading_ic: PropTypes.string,
    leading_ic_onClick: PropTypes.func,
    leading_ic_style: PropTypes.object,
    trailing_ic: PropTypes.string,
    trailing_ic_rawText: PropTypes.bool,
    trailing_ic_onClick: PropTypes.func,
    trailing_ic_style: PropTypes.object,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.string,
    simple_dropdown: PropTypes.bool,
    dropdown: PropTypes.bool,
    dropdownLabel: PropTypes.oneOfType([PropTypes.string,PropTypes.number]),
    renderDropdownLabel: PropTypes.element,
    datepicker: PropTypes.bool,
    textarea: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    variant: PropTypes.oneOf(['info', 'error', 'warning', 'success', '']),
    message: PropTypes.string,
    inputclass: PropTypes.string,
    containerStyle: PropTypes.object,
    innerContainerStyle: PropTypes.object,
    dropdownContainerProps: PropTypes.object
}

const DropdownItem = (props) => {
    return (
        <div
            className={"ds-dropdown-item-container" + (props.active ? " active" : "")}
            key={props.label ? props.label : props.value} 
            id={props.label ? props.label : props.value} 
            data-id={props.value ? props.value : props.label}
            onClick={(e) => {props.onChange(e); props.setDropdownOpen(false)}}
        >
            {props.leading_ic ?
                <div style={{display: 'flex', justifySelf: 'flex-start', flexDirection: 'row', alignItems: 'center', marginLeft: 4, marginRight: 8}}>
                    <Icon name={props.leading_ic} size={16}/>
                </div>
            : null}
            {props.render_leading_ic ?
                <div style={{display: 'flex', justifySelf: 'flex-start', flexDirection: 'row', alignItems: 'center', marginLeft: 4, marginRight: 8}}>
                    {props.render_leading_ic}
                </div>
            : null}
            <div style={{display: 'flex', flex: 1, justifySelf: 'flex-start', flexDirection: 'row', alignItems: 'center'}}>
                <P3Bold>{props.label}</P3Bold>
            </div>
            {props.active ? 
                <div style={{display: 'flex', justifySelf: 'flex-end', flexDirection: 'row', alignItems: 'center', marginRight: 4}}>
                    <Icon name="Check" size={16}/>
                </div>
            : null}
        </div>
    )
}

DropdownItem.defaultProps = {
    active: false,
    label: '',
    leading_ic: '',
    trailing_ic: ''
}

DropdownItem.propTypes = {
    active: PropTypes.bool,
    label: PropTypes.string.isRequired,
    leading_ic: PropTypes.string,
    trailing_ic: PropTypes.string
}

export { Input, DropdownItem };